import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '../../components/SEO';
import TempleMap from '../../components/TempleMap';
import { PageHero, Tabs, Icon, PlaceCard, Skeleton, EmptyState, Button } from '../../components/ui';
import { fetchAllTemples } from '../../services/templeService';
import { ghatRoutes } from '../../routes/ghatRoutes';
import { applyPlaceFilters, sortPlaces } from '../../utils/placeFilters';

const AYODHYA_CENTER = [26.7922, 82.1998];

const LOCAL_IMAGE = [
  [/janmabhoomi|ram mandir/i, '/images/design/hero-ram-mandir.webp'],
  [/hanuman/i, '/images/hanuman/temple-1.webp'],
  [/kanak/i, '/images/kanak-bhawan/temple.jpg'],
  [/nageshwar/i, '/images/ghats/naya-ghat-1.jpg'],
  [/treta|dashrath|sita|mani|gulab|swarg/i, '/images/design/ram-mandir-complex.webp'],
];
const localImage = (name = '') => (LOCAL_IMAGE.find(([re]) => re.test(name)) || [null, '/images/design/ram-mandir-complex.webp'])[1];
const firstHours = (oh) => (oh && (oh.monday || Object.values(oh)[0])) || 'Open daily';
const slugify = (s = '') => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

const toTemple = (t) => ({
  id: t.id,
  type: 'temple',
  name: t.name,
  hindi: '',
  description: t.significance || t.description || '',
  image: t.images?.[0] || localImage(t.name),
  timings: firstHours(t.openingHours),
  popularity: Number(t.popularity) || 0,
  featured: !!t.featured,
  to: `/temples/${t.slug || slugify(t.name)}`,
  coordinates: AYODHYA_CENTER,
});

const GHATS = ghatRoutes.map((g, i) => ({
  id: `ghat-${g.path}`,
  type: 'ghat',
  name: g.name,
  hindi: '',
  description: g.description,
  image: g.image,
  timings: g.timings,
  popularity: 6 - i * 0.3,
  featured: i < 2,
  to: `/ghats/${g.path}`,
  coordinates: g.coordinates,
}));

const TABS = [
  { id: 'all', label: 'All Places' },
  { id: 'temples', label: 'Temples' },
  { id: 'ghats', label: 'Ghats' },
];

export default function Temples() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const [temples, setTemples] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [view, setView] = useState('list');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const tab = params.get('tab') || 'all';
  const q = params.get('q') || '';
  const sort = params.get('sort') || 'popularity';
  const timing = params.get('timing') || 'any';
  const featuredOnly = params.get('featured') === '1';

  const setParam = (key, value, defaults = { tab: 'all', q: '', sort: 'popularity', timing: 'any', featured: '0' }) => {
    const next = new URLSearchParams(params);
    if (value == null || value === '' || value === defaults[key]) next.delete(key); else next.set(key, value);
    setParams(next, { replace: true });
  };
  const clearFilters = () => setParams(new URLSearchParams(tab !== 'all' ? { tab } : {}), { replace: true });

  useEffect(() => {
    let alive = true;
    setLoading(true); setError(false);
    fetchAllTemples({ limit: 100 })
      .then((res) => { if (!alive) return; const list = Array.isArray(res) ? res : res?.data || []; setTemples(list.map(toTemple)); })
      .catch(() => alive && setError(true))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [reloadKey]);

  const all = useMemo(() => [...temples, ...GHATS], [temples]);
  const results = useMemo(() => sortPlaces(applyPlaceFilters(all, { tab, q, timing, featuredOnly }), sort), [all, tab, q, timing, featuredOnly, sort]);
  const counts = useMemo(() => ({ all: all.length, temples: temples.length, ghats: GHATS.length }), [all, temples]);
  const mapPlaces = results.map((p) => ({ ...p, path: p.id, nameEn: p.name }));

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Temples & Sacred Places of Ayodhya',
    url: 'https://ayodhyadham.info/temples',
    numberOfItems: all.length,
    itemListElement: all.slice(0, 20).map((p, i) => ({
      '@type': 'ListItem', position: i + 1,
      item: { '@type': 'TouristAttraction', name: p.name, description: p.description, url: `https://ayodhyadham.info${p.to}`,
        address: { '@type': 'PostalAddress', addressLocality: 'Ayodhya', addressRegion: 'Uttar Pradesh', addressCountry: 'IN' } },
    })),
  };

  return (
    <>
      <SEO
        title="Temples & Sacred Places"
        description="Explore Ram Mandir, Hanuman Garhi, Kanak Bhawan and the sacred ghats of the Saryu — darshan timings, significance and directions."
        canonicalUrl="/temples"
        keywords="temples in Ayodhya, Ram Mandir, Hanuman Garhi, Kanak Bhawan, Ayodhya ghats, darshan timings"
        structuredData={schema}
      />
      <PageHero
        compact
        crumbs={[{ label: t('Home'), to: '/' }, { label: t('Temples & Darshan') }]}
        title={t('Temples & Sacred Places')}
        subtitle={t('Explore the divine heritage of Ayodhya')}
      />

      <div className="container listing-wrap">
        <div className="listing__toolbar">
          <Tabs variant="pill" items={TABS.map((x) => ({ ...x, label: t(x.label), count: counts[x.id] }))} value={tab} onChange={(v) => setParam('tab', v)} ariaLabel={t('Place type')} />
          <div className="listing__tools">
            <label className="search">
              <Icon name="search" size={16} />
              <span className="sr-only">{t('Search temple or place')}</span>
              <input type="search" placeholder={t('Search temple or place…')} value={q} onChange={(e) => setParam('q', e.target.value)} />
            </label>
            <label className="sort">
              <Icon name="arrow-up-down" size={15} />
              <span className="sr-only">{t('Sort by')}</span>
              <select value={sort} onChange={(e) => setParam('sort', e.target.value)}>
                <option value="popularity">{t('Popularity')}</option>
                <option value="name">{t('Name A–Z')}</option>
              </select>
            </label>
            <div className="view-toggle" role="group" aria-label={t('View')}>
              <button type="button" className={view === 'list' ? 'is-active' : ''} onClick={() => setView('list')}><Icon name="layout-grid" size={15} />{t('List')}</button>
              <button type="button" className={view === 'map' ? 'is-active' : ''} onClick={() => setView('map')}><Icon name="map" size={15} />{t('Map')}</button>
            </div>
            <button type="button" className="btn btn--ghost btn--sm filters__toggle" onClick={() => setFiltersOpen((o) => !o)} aria-expanded={filtersOpen}>
              <Icon name="sliders-horizontal" size={15} />{t('Filters')}
            </button>
          </div>
        </div>

        <div className={`listing${view === 'map' ? ' listing--no-side' : ''}`}>
          {view === 'list' && (
            <aside className={`filters${filtersOpen ? ' is-open' : ''}`} aria-label={t('Filters')}>
              <div className="card filters__card">
                <div className="filters__head">
                  <div className="filters__title">{t('Filters')}</div>
                  <button type="button" className="filters__clear" onClick={clearFilters}>{t('Clear all')}</button>
                </div>
                <div className="filters__group">
                  <div className="filters__group-title">{t('Category')}</div>
                  {[['all', t('All Categories'), counts.all], ['temples', t('Temples'), counts.temples], ['ghats', t('Ghats'), counts.ghats]].map(([id, label, n]) => (
                    <label key={id} className="filters__row">
                      <input type="radio" name="cat" checked={tab === id} onChange={() => setParam('tab', id)} />{label}<span className="count">{n}</span>
                    </label>
                  ))}
                </div>
                <div className="filters__group">
                  <div className="filters__group-title">{t('Darshan Timing')}</div>
                  {[['any', t('Any time')], ['morning', t('Morning (5 AM – 12 PM)')], ['afternoon', t('Afternoon (12 – 4 PM)')], ['evening', t('Evening (4 – 11 PM)')]].map(([id, label]) => (
                    <label key={id} className="filters__row">
                      <input type="radio" name="timing" checked={timing === id} onChange={() => setParam('timing', id)} />{label}
                    </label>
                  ))}
                </div>
                <div className="filters__group">
                  <div className="filters__group-title">{t('More')}</div>
                  <label className="filters__row">
                    <input type="checkbox" checked={featuredOnly} onChange={(e) => setParam('featured', e.target.checked ? '1' : '0')} />{t('Featured places only')}
                  </label>
                </div>
              </div>
            </aside>
          )}

          <div>
            <div className="results-meta">
              <span>{loading ? t('Loading places…') : t('{{count}} places', { count: results.length })}</span>
              {q && <button type="button" className="link-arrow" onClick={() => setParam('q', '')}>{t('Clear search')}<Icon name="x" size={14} /></button>}
            </div>

            {view === 'map' ? (
              <div className="map-frame"><TempleMap temples={mapPlaces} /></div>
            ) : loading ? (
              <div className="card-grid">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} height={320} radius="var(--r-lg)" />)}</div>
            ) : error && temples.length === 0 && results.length === 0 ? (
              <EmptyState icon="circle-alert" title={t('Could not load temples')} body={t('The server is not reachable right now. Please try again.')} action={<Button variant="outline" onClick={() => setReloadKey((k) => k + 1)}>{t('Retry')}</Button>} />
            ) : results.length === 0 ? (
              <EmptyState icon="search" title={t('No places match your filters')} body={t('Try a different search term or clear the filters.')} action={<Button variant="outline" onClick={clearFilters}>{t('Clear filters')}</Button>} />
            ) : (
              <>
                {error && <div className="notice notice--info" style={{ marginBottom: 14 }}><Icon name="info" size={18} />{t('Temple data could not be loaded; showing ghats only.')}</div>}
                <div className="card-grid">
                  {results.map((p) => (
                    <PlaceCard
                      key={p.id}
                      image={p.image}
                      title={p.type === 'ghat' ? t(p.name) : p.name}
                      subtitle={p.description}
                      to={p.to}
                      meta={[{ icon: 'clock', text: t(p.timings) }, { icon: p.type === 'ghat' ? 'waves' : 'temple', text: p.type === 'ghat' ? t('Ghat') : t('Temple') }]}
                      badge={p.featured ? t('Featured') : undefined}
                      cta={t('Explore')}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
